/**
 * <pdp-carousel> — drag-scrolling product carousel with a hairline scrollbar.
 *
 * The drag mechanics follow the pattern already proven in
 * sections/products-slider.liquid: a 5px threshold before a pointer-down counts
 * as a drag, and a one-shot capture-phase click suppressor so that finishing a
 * drag over a card does not navigate to it.
 */
class PdpCarousel extends HTMLElement {
	connectedCallback() {
		if (this.initialized) return;
		this.initialized = true;

		this.track = this.querySelector('[data-pdp-carousel-track]');
		this.progress = this.querySelector('[data-pdp-carousel-progress]');
		this.thumb = this.querySelector('[data-pdp-carousel-thumb]');

		if (!this.track) return;

		this.updateThumb = this.updateThumb.bind(this);

		this.track.addEventListener('scroll', this.updateThumb, { passive: true });
		window.addEventListener('resize', this.updateThumb);

		this.bindDrag();
		this.bindThumbDrag();
		this.updateThumb();

		// Card images arrive lazily and change scrollWidth.
		if ('ResizeObserver' in window) {
			this.observer = new ResizeObserver(this.updateThumb);
			this.observer.observe(this.track);
		}
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this.updateThumb);
		if (this.observer) this.observer.disconnect();
	}

	/**
	 * Standard scrollbar maths: the thumb is as wide a fraction of the rule as
	 * the viewport is of the content. The Figma's 83.8% is what this produces
	 * for ~5 cards in a 1900px viewport — it is not hardcoded.
	 */
	updateThumb() {
		if (!this.thumb || !this.progress) return;

		const { scrollWidth, clientWidth, scrollLeft } = this.track;
		const railWidth = this.progress.clientWidth;

		if (scrollWidth <= clientWidth + 1) {
			this.progress.hidden = true;
			return;
		}
		this.progress.hidden = false;

		const ratio = clientWidth / scrollWidth;
		const thumbWidth = Math.max(railWidth * ratio, 24);
		const maxOffset = railWidth - thumbWidth;
		const progressed = scrollLeft / (scrollWidth - clientWidth);

		this.thumb.style.setProperty('--thumb-width', `${thumbWidth}px`);
		this.thumb.style.setProperty('--thumb-offset', `${maxOffset * progressed}px`);
	}

	bindDrag() {
		const DRAG_THRESHOLD = 5;
		let pointerId = null;
		let startX = 0;
		let startScroll = 0;
		let moved = false;

		const end = (event) => {
			if (pointerId === null) return;
			this.track.classList.remove('is-dragging');
			try {
				this.track.releasePointerCapture(pointerId);
			} catch (e) {
				/* pointer already released */
			}
			pointerId = null;

			// Suppress exactly one click so ending a drag over a card does not
			// follow its link. Capture phase, because the <a> is the target.
			if (moved) {
				const suppress = (clickEvent) => {
					clickEvent.preventDefault();
					clickEvent.stopPropagation();
				};
				this.track.addEventListener('click', suppress, {
					capture: true,
					once: true,
				});
				// If no click follows, drop the listener again.
				setTimeout(() => {
					this.track.removeEventListener('click', suppress, { capture: true });
				}, 0);
			}
		};

		// No capture here — it retargets the click to the track and kills the card link.
		this.track.addEventListener('pointerdown', (event) => {
			if (event.pointerType === 'mouse' && event.button !== 0) return;
			pointerId = event.pointerId;
			startX = event.clientX;
			startScroll = this.track.scrollLeft;
			moved = false;
		});

		this.track.addEventListener('pointermove', (event) => {
			if (pointerId === null) return;
			const delta = event.clientX - startX;
			if (!moved && Math.abs(delta) < DRAG_THRESHOLD) return;
			if (!moved) {
				moved = true;
				this.track.classList.add('is-dragging');
				this.track.setPointerCapture(pointerId);
			}
			this.track.scrollLeft = startScroll - delta;
		});

		this.track.addEventListener('pointerup', end);
		this.track.addEventListener('pointercancel', end);

		// Native image dragging would otherwise hijack the gesture.
		this.track.addEventListener('dragstart', (event) => event.preventDefault());
	}

	/** The rule itself is draggable, like a real scrollbar. */
	bindThumbDrag() {
		if (!this.progress) return;

		const scrollToPointer = (clientX) => {
			const rail = this.progress.getBoundingClientRect();
			const ratio = Math.min(Math.max((clientX - rail.left) / rail.width, 0), 1);
			this.track.scrollLeft =
				ratio * (this.track.scrollWidth - this.track.clientWidth);
		};

		let dragging = false;

		this.progress.addEventListener('pointerdown', (event) => {
			dragging = true;
			this.progress.setPointerCapture(event.pointerId);
			scrollToPointer(event.clientX);
		});

		this.progress.addEventListener('pointermove', (event) => {
			if (dragging) scrollToPointer(event.clientX);
		});

		const stop = () => {
			dragging = false;
		};
		this.progress.addEventListener('pointerup', stop);
		this.progress.addEventListener('pointercancel', stop);
	}
}

if (!customElements.get('pdp-carousel')) {
	customElements.define('pdp-carousel', PdpCarousel);
}
